<template>
  <el-dialog
    :title="title"
    size="small"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :visible="true"
  >
    <el-form
      ref="user-form"
      :model="userObj"
      :rules="rules"
      label-width="180px"
    >
      <el-form-item label="Username" prop="username">
        <el-input
          v-model="userObj.username"
          placeholder="Enter the username"
          :disabled="!hasCreatePermission"
          class="width75"
        />
      </el-form-item>
      <el-form-item label="Email Address" prop="email">
        <el-input
          v-model="userObj.email"
          placeholder="Enter the email address"
          :disabled="!hasCreatePermission"
          class="width75"
        />
      </el-form-item>
      <el-form-item label="First Name" prop="first_name">
        <el-input
          v-model="userObj.first_name"
          placeholder="Enter the first name"
          :disabled="!hasCreatePermission"
          class="width75"
        />
      </el-form-item>
      <el-form-item label="Last Name" prop="last_name">
        <el-input
          v-model="userObj.last_name"
          placeholder="Enter the last name"
          :disabled="!hasCreatePermission"
          class="width75"
        />
      </el-form-item>
      <el-form-item label="Roles" prop="groups">
        <el-select
          v-model="userObj.groups"
          placeholder="Select role(s)"
          multiple
          filterable
          :loading="groupLoading"
          :disabled="!hasCreatePermission"
          remote
          :remote-method="loadGroupList"
          class="width75"
        >
          <el-option
            v-for="group in groupList"
            :key="group.id"
            :label="group.name"
            :value="group.id"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Departments" prop="departments">
        <el-select
          v-model="userObj.departments"
          placeholder="Select departments(s)"
          multiple
          filterable
          :loading="departmentLoading"
          :disabled="!hasCreatePermission"
          remote
          :remote-method="loadDepartmentList"
          class="width75"
        >
          <el-option
            v-for="department in departmentList"
            :key="department.id"
            :label="department.name"
            :value="department.id"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Active" prop="is_active" class="flex-grow-1">
        <el-checkbox :disabled="!hasCreatePermission" v-model="userObj.is_active" />
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button :disabled="saving" @click="cancel">Cancel</el-button>
      <el-button :disabled="!hasCreatePermission" :loading="saving" type="primary" @click="save">
        {{ userObj.id ? 'Update' : 'Submit' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { getHeaders, buildUrlWithQuery, buildUrl } from '@/utils/api'
import Vue from 'vue'

export default {
  name: 'UserForm',
  props: ['userDetails', 'hasCreatePermission'],
  data () {
    return {
      loading: false,
      saving: false,
      userObj: {
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        is_active: true,
        groups: [],
        departments: []
      },
      groupList: [],
      departmentList: [],
      groupLoading: false,
      departmentLoading: false,
      rules: {
        username: {
          required: true,
          validator: (rule, value, callback) => {
            const regex = /^[a-zA-Z0-9@.+_-]*$/
            if (!value) {
              callback(new Error('Please specify a user name.'))
            } else if (!regex.test(value)) {
              callback(
                new Error(
                  'Please specify a user name which contain only letters, numbers, and @/./+/-/_ characters.'
                )
              )
            } else {
              callback()
            }
          },
          trigger: 'blur'
        },
        email: {
          required: true,
          validator: (rule, value, callback) => {
            const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            if (!value) {
              callback(new Error('Please specify a user name.'))
            } else if (!regex.test(value)) {
              callback(
                new Error(
                  'Please specify valid email.'
                )
              )
            } else {
              callback()
            }
          },
          trigger: 'blur'
        },
        first_name: {
          required: true,
          validator: (rule, value, callback) => {
            if (!value) {
              callback(new Error('Please specify a user name.'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        },
        last_name: {
          required: true,
          validator: (rule, value, callback) => {
            if (!value) {
              callback(new Error('Please specify a user name.'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        },
        groups: {
          required: true,
          validator: (rule, value, callback) => {
            if (!value.length) {
              callback(new Error('Please select atleast one role'))
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      }
    }
  },
  created () {
    this.loadGroupList()
    this.loadDepartmentList()
    if (this.userDetails) {
      this.userObj = {
        ...this.userDetails
      }
    }
  },
  computed: {
    isNew () {
      return !this.userDetails
    },
    title () {
      return this.isNew ? 'New User' : 'Edit User'
    }
  },
  methods: {
    async loadGroupList (search) {
      this.groupLoading = true
      try {
        const instanceUrl = this.$store.getters.getActiveInstance.url
        const headers = getHeaders(this.$store.getters.getToken)

        let payload = { is_active: true }
        if (search) payload = { ...payload, search }

        let { data } = await Vue.http.get(
          buildUrlWithQuery('roles', payload, instanceUrl),
          { headers }
        )

        this.groupList = data.results || []
      } catch (e) {
        console.log(e)
      }
      this.groupLoading = false
    },
    async loadDepartmentList (search) {
      this.groupLoading = true
      try {
        const instanceUrl = this.$store.getters.getActiveInstance.url
        const headers = getHeaders(this.$store.getters.getToken)

        let payload = { is_active: true }
        if (search) payload = { ...payload, search }

        let { data } = await Vue.http.get(
          buildUrlWithQuery('departments', payload, instanceUrl),
          { headers }
        )

        this.departmentList = data.results || []
      } catch (e) {
        console.log(e)
      }
      this.groupLoading = false
    },
    async createUser () {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)

      Vue.http.post(buildUrl('users', null, instanceUrl), this.userObj, { headers })
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            this.$notify({
              title: 'Saved',
              message: 'User created successfully and Email sent to setup the password',
              type: 'success',
              customClass: 'successAlert'
            })
            this.$emit('onSave')
            this.cancel()
          }
          this.saving = false
        })
        .catch(e => {
          this.saving = false
        })
    },
    async updateUser () {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)

      let { userObj, userDetails } = this
      let { id } = userObj
      let userKeys = [
        'username',
        'groups',
        'email',
        'first_name',
        'last_name',
        'departments',
        'is_active'
      ]
      let payload = {}

      userKeys.forEach(key => {
        if (key !== 'groups' || key !== 'departments') {
          if (userDetails[key] !== userObj[key]) {
            payload[key] = this.userObj[key]
          }
        } else {
          if (userDetails[key].length !== userObj[key].length) {
            payload[key] = this.userObj[key]
          } else {
            const initialGrpValue = userDetails[key]
              .slice()
              .sort((a, b) => a - b)
            const updatedGrpValue = userObj[key].slice().sort((a, b) => a - b)

            // Compare the sorted arrays
            let isEqual = initialGrpValue.every(
              (value, index) => value === updatedGrpValue[index]
            )

            if (!isEqual) payload[key] = this.userObj[key]
          }
        }
      })
      if (isEmpty(payload)) this.cancel()

      payload.id = id

      Vue.http.patch(buildUrl(`users/${id}`, null, instanceUrl), payload, { headers })
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            this.$notify({
              title: 'Saved',
              message: 'User updated successfully',
              type: 'success',
              customClass: 'successAlert'
            })
            this.$emit('onSave')
            this.cancel()
          }
          this.saving = false
        })
        .catch(e => {
          this.saving = false
        })
    },
    save () {
      this.$refs['user-form'].validate(async valid => {
        if (!valid) return

        this.saving = true
        try {
          if (this.isNew) {
            await this.createUser()
          } else {
            await this.updateUser()
          }
        } catch (e) {
        }
      })
    },
    cancel () {
      this.$emit('onClose')
    }
  }
}
</script>
<style scoped>
.width75 {
  width: 75%;
}
.el-dialog--small {
  top: 5% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Users/UserForm.vue